import {
  SheetViewAction,
  workbookViewReducer,
  WorkbookViewState,
} from "./sheetViewState";

/**
 * Undo and redo over the view state. Named viewHistory rather than undo because
 * the word "undo" is already taken twice in this codebase - by RTK Query
 * optimistic rollbacks and by the server-side "un-mark" action - and a grep for
 * either should not land here.
 *
 * Only what a person did to the data is recorded. Switching sheets, restoring a
 * saved view and seeding a sheet's defaults are not: an undo that quietly moved
 * you to another tab would be worse than no undo.
 */

export interface ViewHistory {
  past: WorkbookViewState[];
  present: WorkbookViewState;
  future: WorkbookViewState[];
}

export const MAX_HISTORY = 30;

export type ViewHistoryAction =
  | SheetViewAction
  | { type: "historyUndo" }
  | { type: "historyRedo" };

const NOT_RECORDED: Record<string, true> = {
  hydrate: true,
  ensureSheet: true,
  setActiveSheet: true,
};

export function initialViewHistory(present: WorkbookViewState): ViewHistory {
  return { past: [], present, future: [] };
}

export function canUndo(history: ViewHistory): boolean {
  return history.past.length > 0;
}

export function canRedo(history: ViewHistory): boolean {
  return history.future.length > 0;
}

export function viewHistoryReducer(
  history: ViewHistory,
  action: ViewHistoryAction,
): ViewHistory {
  if (action.type === "historyUndo") {
    if (history.past.length === 0) return history;
    const previous = history.past[history.past.length - 1];
    return {
      past: history.past.slice(0, history.past.length - 1),
      present: previous,
      future: [history.present].concat(history.future).slice(0, MAX_HISTORY),
    };
  }

  if (action.type === "historyRedo") {
    if (history.future.length === 0) return history;
    const next = history.future[0];
    return {
      past: history.past.concat([history.present]).slice(-MAX_HISTORY),
      present: next,
      future: history.future.slice(1),
    };
  }

  const present = workbookViewReducer(history.present, action);
  // The reducer returns the same object when an action changes nothing, so an
  // undo never has to step over a no-op.
  if (present === history.present) return history;
  if (NOT_RECORDED[action.type]) return { ...history, present };

  return {
    past: history.past.concat([history.present]).slice(-MAX_HISTORY),
    present,
    future: [],
  };
}
